/**
 * BlockedAccountOverlay
 * 
 * Full-screen overlay that blocks platform access for:
 * - Delinquent accounts (billingStatus === 'overdue' or 'suspended')
 * - Expired trials (isTrialExpired === true)
 * 
 * Behavior:
 * - SuperAdmins bypass entirely (they manage the platform)
 * - Admins (level 50+) see payment link / upgrade actions
 * - Regular users see "contact your administrator" message
 * - Auto-dismisses when billingStatus returns to 'ok' (via socket event + refreshFeatures)
 * - Polls refreshFeatures every 60s as fallback when socket is unreliable
 * - Logout button always visible for all user types
 */
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEmbed } from '../contexts/EmbedContext';
import { ShieldAlert, CreditCard, ExternalLink, Clock, Sparkles, LogOut, RefreshCw } from 'lucide-react';

export default function BlockedAccountOverlay() {
  const {
    billingStatus,
    billingPaymentLink,
    isTrialExpired,
    isSuperAdmin,
    isAdmin,
    refreshFeatures,
    logout,
  } = useAuth();
  // EMB-13: este overlay é irmão de InternalLayout (T15), não filho — por
  // isso Sair/Billing (incl. o upsell de trial) precisam do próprio corte
  // aqui; sem eles, nada aqui menciona a marca do parceiro, então não há
  // literal "TouchWorks" a trocar por EMB-16 além do que já é escondido.
  const { isEmbed } = useEmbed();

  // Determine blocking reason
  const isBillingBlocked = billingStatus === 'overdue' || billingStatus === 'suspended';
  const isBlocked = isBillingBlocked || isTrialExpired;

  // Poll refreshFeatures every 60s while blocked (fallback for socket issues)
  useEffect(() => {
    if (!isBlocked) return;
    const interval = setInterval(() => {
      refreshFeatures().catch(() => {});
    }, 60_000);
    return () => clearInterval(interval);
  }, [isBlocked, refreshFeatures]);

  // SuperAdmins always bypass
  if (isSuperAdmin()) return null;
  if (!isBlocked) return null;

  const admin = isAdmin();
  const isSuspended = billingStatus === 'suspended';
  const genericUserBillingCopy = {
    title: 'Acesso temporariamente indisponível',
    badge: 'Manutenção administrativa',
    message: 'No momento seu acesso está temporariamente indisponível. Entre em contato com o administrador da sua empresa para verificar a previsão de retorno.',
  };

  // Choose variant based on blocking reason
  // Billing takes precedence over trial expired
  const variant = isBillingBlocked
    ? (isSuspended ? 'suspended' : 'overdue')
    : 'trial_expired';

  const config = {
    suspended: {
      icon: ShieldAlert,
      iconBg: 'bg-red-500/15 border-2 border-red-500/30',
      iconColor: 'text-red-700 dark:text-red-400',
      title: 'Conta Suspensa',
      badge: 'Suspensa por inadimplência',
      badgeColor: 'text-red-700 dark:text-red-400',
      adminMessage: 'Sua conta foi suspensa por falta de pagamento. Para reativar o acesso à plataforma, regularize a situação financeira.',
      userMessage: 'Sua conta está temporariamente suspensa. Entre em contato com o administrador da sua empresa para regularizar a situação.',
    },
    overdue: {
      icon: ShieldAlert,
      iconBg: 'bg-red-500/15 border-2 border-red-500/30',
      iconColor: 'text-red-700 dark:text-red-400',
      title: 'Conta Bloqueada',
      badge: 'Pagamento pendente',
      badgeColor: 'text-red-700 dark:text-red-400',
      adminMessage: 'Sua conta está bloqueada devido a uma pendência financeira. Efetue o pagamento para restaurar o acesso.',
      userMessage: 'Sua conta está temporariamente bloqueada. Entre em contato com o administrador da sua empresa para regularizar a situação.',
    },
    trial_expired: {
      icon: Clock,
      iconBg: 'bg-violet-500/15 border-2 border-violet-500/30',
      iconColor: 'text-violet-700 dark:text-violet-400',
      title: 'Período de Avaliação Encerrado',
      badge: 'Trial expirado',
      badgeColor: 'text-violet-700 dark:text-violet-400',
      adminMessage: 'Seu período de avaliação gratuita chegou ao fim. Para continuar utilizando a plataforma, contrate um plano.',
      userMessage: 'O período de avaliação da sua empresa expirou. Entre em contato com o administrador para contratar um plano.',
    },
  };

  const c = config[variant];
  const Icon = c.icon;
  const title = !admin && isBillingBlocked ? genericUserBillingCopy.title : c.title;
  const badge = !admin && isBillingBlocked ? genericUserBillingCopy.badge : c.badge;
  let message = c.userMessage;
  if (admin) {
    message = c.adminMessage;
  } else if (isBillingBlocked) {
    message = genericUserBillingCopy.message;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop: dark + blur */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Dialog card */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-surface border border-border-strong rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-300">
        {/* Header icon */}
        <div className="flex flex-col items-center pt-8 pb-4 px-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${c.iconBg}`}>
            <Icon className={`w-8 h-8 ${c.iconColor}`} />
          </div>

          <h2 className="text-xl font-bold text-foreground-strong text-center">
            {title}
          </h2>

          <div className="mt-1 px-3 py-0.5 rounded-full bg-surface-active border border-border-strong">
            <span className={`text-xs font-medium ${c.badgeColor}`}>
              {badge}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          {admin ? (
            <div className="space-y-4">
              <p className="text-sm text-foreground-muted text-center leading-relaxed">
                {message}
              </p>

              {/* Billing blocked: show payment link — self-service billing
                  never appears inside the embed shell (EMB-13). */}
              {isBillingBlocked && billingPaymentLink && !isEmbed && (
                <a
                  href={billingPaymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-900/20"
                >
                  <CreditCard className="w-5 h-5" />
                  Efetuar Pagamento
                  <ExternalLink className="w-4 h-4 opacity-60" />
                </a>
              )}

              {/* Trial expired: show upgrade prompt — self-service billing
                  never appears inside the embed shell (EMB-13), so this is
                  unreachable there and the href stays the literal TouchWorks
                  copy unchanged (byte-identical to today, EMB-16). */}
              {variant === 'trial_expired' && !isEmbed && (
                <div className="space-y-3">
                  <a
                    href="https://wa.me/5511999999999?text=Olá! Gostaria de contratar um plano TouchWorks."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-cyan-900/20"
                  >
                    <Sparkles className="w-5 h-5" />
                    Contratar Plano
                    <ExternalLink className="w-4 h-4 opacity-60" />
                  </a>
                </div>
              )}

              <p className="text-xs text-foreground-muted text-center">
                {isBillingBlocked
                  ? 'Após a confirmação do pagamento, o acesso será restaurado automaticamente.'
                  : 'Após a ativação do plano, o acesso será liberado automaticamente.'}
              </p>

              {/* Auto-refresh indicator */}
              <div className="flex items-center justify-center gap-1.5 text-xs text-foreground-muted/60">
                <RefreshCw className="w-3 h-3" />
                Verificando status automaticamente...
              </div>

              {/* Logout — ausente no shell embutido (EMB-13), mesma regra do menu do avatar. */}
              {!isEmbed && (
                <button
                  onClick={logout}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-surface-active hover:bg-surface-hover border border-border-strong text-foreground-muted font-medium rounded-xl transition-all text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Sair da conta
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-foreground-muted text-center leading-relaxed">
                {message}
              </p>

              <div className="flex items-center justify-center gap-2 py-3 px-4 bg-surface-active border border-border-strong rounded-xl">
                <ShieldAlert className="w-5 h-5 text-foreground-muted" />
                <span className="text-sm text-foreground-muted">
                  Contate o administrador
                </span>
              </div>

              {/* Auto-refresh indicator */}
              <div className="flex items-center justify-center gap-1.5 text-xs text-foreground-muted/60">
                <RefreshCw className="w-3 h-3" />
                Verificando status automaticamente...
              </div>

              {/* Logout — ausente no shell embutido (EMB-13), mesma regra do menu do avatar. */}
              {!isEmbed && (
                <button
                  onClick={logout}
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-surface-active hover:bg-surface-hover border border-border-strong text-foreground-muted font-medium rounded-xl transition-all text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  Sair da conta
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
